import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

/**
 * The dashboard's subscriptions carousel, which the design draws as a deck
 * rather than a strip (the notifications panel draws the same deck but empties
 * it instead of turning it, and has its own — see `NotificationsPanel.jsx`):
 * one card face-on, with the edges of the next two peeking out below it.
 *
 * The two behind-layers are decoration, not content. They are drawn as empty
 * rounded rectangles and hidden from the accessibility tree, because the real
 * second and third items are not on screen — a screen reader that announced
 * them would be describing a picture of a card, and one the reader cannot
 * reach. Only the front card is rendered, and the arrows change which item it
 * is.
 *
 * They also disappear when there is nothing behind: a deck of one drawn with
 * two shadows under it promises items that do not exist.
 *
 * `layers` and `stackClassName` exist for a front card narrower than the frame,
 * whose layers have to be inset from that card, not from the frame — so the
 * stack takes the card's width, and the layers take that design's own insets,
 * drops, radii and borders. There are no layers unless they are passed: the
 * subscriptions panel passes the design's two.
 *
 * Each layer is the front card shrunk and pushed down: `inset` from each side,
 * `top` from the card's top and `drop` below its bottom. Only the strip below
 * the card ever shows. The stack keeps room under the card for the deepest
 * drop, so no layer breaks through the frame's bottom edge, and the arrows stay
 * at the frame's middle, where the design puts them.
 *
 * Layers are listed nearest first and painted deepest first, as the design
 * orders them. Painted the other way, the narrower deep card lands on top of
 * the wider one and hides its bottom edge — what is left reads as one stray
 * strip hanging under the card instead of two cards behind it.
 *
 * DIRECTION
 *
 * The arrows sit at the inline edges and point outward, which is what the
 * design draws and what a reader of either language expects. That makes the
 * one at the inline start "previous" in both — in Arabic that is the right-hand
 * edge, in English the left — so the glyph is flipped under `rtl:` rather than
 * swapped, and neither button moves.
 *
 * The deck wraps at both ends rather than disabling an arrow. Both panels hold
 * a handful of items at most, and a dead control at the edge of a three-card
 * deck reads as a bug rather than a boundary.
 *
 * TURNING
 *
 * A step slides the new card in from the side the reader is heading towards
 * while the old one slides out the other way — "next" comes in from the left in
 * Arabic and from the right in English. For those 450ms the old card is the one
 * exception to "only the front card is rendered": it is laid over the new one,
 * hidden from the accessibility tree and `inert`, and dropped when its animation
 * ends. Under `prefers-reduced-motion` neither animates and the old card is
 * never shown; the deck simply changes face.
 *
 * `autoPlay` is a delay in milliseconds, and turns the deck on its own. It stops
 * while there is a reason to hold the card still: the pointer is over the deck,
 * keyboard focus is inside it, the tab is in the background, or the caller says
 * `paused` — the subscriptions panel does while one of its card's dialogues is
 * open. A mouse click on an arrow does not count as focus, or one click would
 * stop the deck for good. Every step restarts the count, so a card reached by
 * the arrows gets its full time on screen. While it plays the front card is not
 * a live region; once it holds still, a step taken by hand is announced.
 */
const ARROW =
    'tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-z-10 tw-flex tw-size-[24px] ' +
    'tw-items-center tw-justify-center tw-rounded-full tw-bg-white ' +
    'tw-border tw-border-solid tw-border-v2-student-arrow tw-shadow-v2-card ' +
    'tw-text-v2-neutral-900 hover:tw-bg-v2-neutral-50 tw-transition-colors ' +
    'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand';

/** How far a card travels as it turns: enough to read as a slide, too little to clear the frame. */
const SHIFT = 40;

const isKeyboardFocus = (element) => {
    try {
        return element.matches(':focus-visible');
    } catch {
        return true;
    }
};

const Deck = ({
    items = [],
    children,
    label,
    layers = [],
    stackClassName = 'tw-w-full',
    className = '',
    autoPlay = 0,
    paused = false,
}) => {
    const { t, i18n } = useTranslation();
    // `turn` counts steps. It keys the two faces, so a step always restarts
    // their animations, and it restarts the auto-play count.
    const [slide, setSlide] = useState({ index: 0, leaving: null, by: 0, turn: 0 });
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [hidden, setHidden] = useState(() => document.hidden);
    const count = items.length;
    const { index, leaving, by, turn } = slide;

    // A list that shrinks under the reader — a notification cleared, a
    // subscription that lapsed — would otherwise leave the deck pointing past
    // its own end and rendering nothing.
    useEffect(() => {
        if (index > count - 1) setSlide((s) => ({ index: 0, leaving: null, by: 0, turn: s.turn + 1 }));
    }, [count, index]);

    const step = useCallback((delta) => setSlide((s) => ({
        index: (s.index + delta + count) % count,
        leaving: s.index,
        by: delta,
        turn: s.turn + 1,
    })), [count]);

    useEffect(() => {
        if (!autoPlay) return undefined;
        const onVisibility = () => setHidden(document.hidden);
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [autoPlay]);

    const playing = autoPlay > 0 && count > 1 && !paused && !hovered && !focused && !hidden;

    useEffect(() => {
        if (!playing) return undefined;
        const timer = setTimeout(() => step(1), autoPlay);
        return () => clearTimeout(timer);
    }, [playing, turn, autoPlay, step]);

    if (!count) return null;

    const current = items[Math.min(index, count - 1)];
    const outgoing = leaving !== null && leaving < count ? items[leaving] : undefined;

    // Nearest first, as passed. A deck of two has only one card behind it.
    const shown = count > 1 ? layers.slice(0, count - 1) : [];
    const depth = shown.reduce((deepest, { drop }) => Math.max(deepest, drop), 0);

    // Positive travels right. "Next" moves the deck towards the reading
    // direction's end, which is the left-hand side in Arabic.
    const rtl = i18n.language?.startsWith('ar');
    const sign = (by < 0 ? -1 : 1) * (rtl ? -1 : 1);
    const travel = { '--v2-deck-from': `${sign * SHIFT}px`, '--v2-deck-to': `${-sign * SHIFT}px` };

    const dropOutgoing = (event) => {
        if (event.target !== event.currentTarget) return;
        setSlide((s) => (s.turn === turn ? { ...s, leaving: null } : s));
    };

    return (
        <div
            className={
                'tw-relative tw-flex tw-items-center tw-justify-center tw-bg-white ' +
                'tw-border-[0.518px] tw-border-solid tw-border-v2-line tw-shadow-v2-sm ' +
                `tw-rounded-[11.658px] tw-px-[22px] tw-py-[16px] ${className}`
            }
            role="group"
            aria-roledescription="carousel"
            aria-label={label}
            onPointerEnter={(event) => event.pointerType === 'mouse' && setHovered(true)}
            onPointerLeave={(event) => event.pointerType === 'mouse' && setHovered(false)}
            onFocus={(event) => isKeyboardFocus(event.target) && setFocused(true)}
            onBlur={(event) => !event.currentTarget.contains(event.relatedTarget) && setFocused(false)}
        >
            {count > 1 && (
                <button
                    type="button"
                    onClick={() => step(-1)}
                    aria-label={t('v2.student.deck.previous')}
                    className={ARROW}
                    style={{ insetInlineStart: '8px' }}
                >
                    <LuChevronLeft size={11} className="rtl:tw-rotate-180" />
                </button>
            )}

            <div className={`tw-relative ${stackClassName}`} style={{ paddingBottom: depth }}>
                {shown.slice().reverse().map(({ inset, top, drop, radius, border = 1 }) => (
                    <div
                        key={inset}
                        aria-hidden
                        className="tw-absolute tw-bg-white tw-shadow-v2-card tw-border-solid tw-border-v2-ground-300"
                        style={{
                            insetInlineStart: inset,
                            insetInlineEnd: inset,
                            top: top ?? drop,
                            // From the stack's bottom, which is `depth` below the card's.
                            bottom: depth - drop,
                            borderRadius: radius,
                            borderWidth: border,
                        }}
                    />
                ))}

                {/* Positioned so it paints over the two above without a
                    z-index: among positioned siblings, DOM order decides. The
                    same rule puts the incoming face over the outgoing one. */}
                <div className="tw-relative" style={travel} aria-live={playing ? 'off' : 'polite'}>
                    {outgoing !== undefined && (
                        <div
                            key={`out-${turn}`}
                            aria-hidden
                            inert=""
                            onAnimationEnd={dropOutgoing}
                            className={
                                'tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 ' +
                                'tw-animate-v2-deck-out motion-reduce:tw-hidden'
                            }
                        >
                            {children(outgoing, leaving)}
                        </div>
                    )}

                    <div
                        key={`in-${turn}`}
                        className={`tw-relative ${by ? 'tw-animate-v2-deck-in motion-reduce:tw-animate-none' : ''}`}
                    >
                        {children(current, index)}
                    </div>
                </div>
            </div>

            {count > 1 && (
                <button
                    type="button"
                    onClick={() => step(1)}
                    aria-label={t('v2.student.deck.next')}
                    className={ARROW}
                    style={{ insetInlineEnd: '8px' }}
                >
                    <LuChevronRight size={11} className="rtl:tw-rotate-180" />
                </button>
            )}
        </div>
    );
};

export default Deck;
